/**
 * BESTSELLERS — RANKING FROM OUTSIDE
 *
 * Contract with `GET /merchants/{id}/bestsellers` (public): up to 20 items,
 * already ordered by summed quantity (desc) over the last 7 rolling days.
 * The storefront does NOT reorder. `merchantItemId` is the catalog id
 * (`itemOffer.itemId`); joining is the consumer's job.
 */
export type BestsellerEntry = {
  /** Catalog id — join key with `itemOffer.itemId`. */
  merchantItemId: string;
  name: string;
  quantity: number;
};

/**
 * DEFENSIVE parser for `GET /merchants/{id}/bestsellers`. Never throws.
 * Unexpected shapes are DROPPED so one bad item cannot wipe the other nineteen.
 */
export function parseBestsellersResponse(data: unknown): BestsellerEntry[] {
  if (!data || typeof data !== "object") return [];

  const items = (data as { items?: unknown }).items;
  if (!Array.isArray(items)) return [];

  const entries: BestsellerEntry[] = [];
  for (const raw of items) {
    if (!raw || typeof raw !== "object") continue;

    const merchantItemId = (raw as { merchantItemId?: unknown }).merchantItemId;
    if (typeof merchantItemId !== "string" || merchantItemId.length === 0) {
      continue;
    }

    const name = (raw as { name?: unknown }).name;
    const quantity = (raw as { quantity?: unknown }).quantity;

    entries.push({
      merchantItemId,
      name: typeof name === "string" ? name : "",
      quantity: typeof quantity === "number" && Number.isFinite(quantity) ? quantity : 0,
    });
  }

  return entries;
}
